import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAutoSave } from '../hooks/useAutoSave';
import AutoSaveIndicator from '../components/ui/AutoSaveIndicator';
import { Plus, Trash2, Loader2, GripVertical } from 'lucide-react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { useEvent } from '../contexts/EventContext';
import apiService from '../services/api.service';
import { toast } from 'sonner';

interface Segment {
  uid: string;
  station_name: string;
}

const CourseStations: React.FC = () => {
  const { t } = useTranslation();
  const { selectedEventId } = useEvent();
  const [segments, setSegments] = useState<Segment[]>([]);
  const [loading, setLoading] = useState(false);
  const uidRef = useRef(0);
  // Le blur lit la liste À JOUR : `segments` capturé dans le rendu serait
  // celui d'avant la dernière frappe.
  const segmentsRef = useRef<Segment[]>([]);
  segmentsRef.current = segments;
  const newUid = () => `seg-${uidRef.current++}`;

  const load = useCallback(async () => {
    if (!selectedEventId) return;
    setLoading(true);
    try {
      const data = await apiService.getCourseSegments(selectedEventId);
      setSegments((data.segments || []).map((s: any) => ({ uid: newUid(), station_name: s.station_name })));
    } catch (e) {
      toast.error(t('courseStations.loadError'));
    } finally {
      setLoading(false);
    }
  }, [selectedEventId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const update = (uid: string, station_name: string) =>
    setSegments((prev) => prev.map((s) => (s.uid === uid ? { ...s, station_name } : s)));

  const remove = (uid: string) =>
    setSegments((prev) => {
      const liste = prev.filter((s) => s.uid !== uid);
      enregistrer(liste);
      return liste;
    });

  const add = () => setSegments((prev) => [...prev, { uid: newUid(), station_name: '' }]);

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    setSegments((prev) => {
      const next = [...prev];
      const [moved] = next.splice(result.source.index, 1);
      next.splice(result.destination!.index, 0, moved);
      enregistrer(next);
      return next;
    });
  };

  /**
   * Le parcours s'enregistre au fur et à mesure : un nom part au blur, un
   * retrait et un déplacement tout de suite.
   *
   * L'écriture porte la LISTE ENTIÈRE, et c'est normal : l'ordre des stations
   * EST la donnée, on ne peut pas l'exprimer station par station. Une station
   * sans nom est écartée — c'est une ligne qu'on vient d'ajouter et qu'on n'a
   * pas encore remplie, pas un parcours qui en contiendrait une vide.
   *
   * ⚠️ Pas de rechargement après écriture : `load()` régénère les `uid`, ce
   * qui remonterait les champs et ferait perdre le focus en pleine saisie.
   */
  const auto = useAutoSave<{ segments: Segment[] }>(useCallback(async ({ segments: liste }) => {
    if (!selectedEventId) return;
    // laps conservé en DB (toujours 0) — non utilisé fonctionnellement, colonne retirée de l'UI.
    await apiService.saveCourseSegments(
      selectedEventId,
      liste.map((s) => ({ station_name: s.station_name.trim(), laps: 0 }))
          .filter((s) => s.station_name),
    );
  }, [selectedEventId]), {
    onError: (e: any) => toast.error(e?.response?.data?.detail || t('courseStations.saveError')),
  });

  const enregistrer = (liste: Segment[]) => { void auto.commit({ segments: liste }); };

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-3xl">
      <p className="text-sm text-muted-foreground">{t('courseStations.intro')}</p>

      <Card>
        <CardContent className="pt-6 space-y-3">
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="animate-spin" /></div>
          ) : (
            <>
              {segments.length === 0 && (
                <div className="text-center py-6 text-muted-foreground text-sm">{t('courseStations.empty')}</div>
              )}

              {segments.length > 0 && (
                <div className="flex items-center gap-2 px-1 text-xs text-muted-foreground">
                  <span className="w-5" />
                  <span className="flex-1">{t('courseStations.stationName')}</span>
                  <span className="w-9" />
                </div>
              )}

              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="course-stations">
                  {(dropProvided) => (
                    <div ref={dropProvided.innerRef} {...dropProvided.droppableProps} className="space-y-2">
                      {segments.map((s, i) => (
                        <Draggable key={s.uid} draggableId={s.uid} index={i}>
                          {(dragProvided, snapshot) => (
                            <div
                              ref={dragProvided.innerRef}
                              {...dragProvided.draggableProps}
                              className={`flex items-center gap-2 rounded-md ${snapshot.isDragging ? 'bg-white/[0.08] ring-1 ring-white/30' : ''}`}
                            >
                              <span
                                {...dragProvided.dragHandleProps}
                                className="flex h-9 w-5 cursor-grab items-center justify-center text-muted-foreground active:cursor-grabbing"
                                title={t('courseStations.dragHint')}
                              >
                                <GripVertical size={16} />
                              </span>
                              <div className="flex-1">
                                <Input
                                  value={s.station_name}
                                  placeholder={t('courseStations.stationPlaceholder')}
                                  onChange={(e) => update(s.uid, e.target.value)}
                                  onBlur={() => enregistrer(segmentsRef.current)}
                                />
                              </div>
                              <Button variant="ghost" size="icon" className="text-destructive" onClick={() => remove(s.uid)}>
                                <Trash2 size={16} />
                              </Button>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {dropProvided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>

              <div className="flex items-center justify-between pt-2">
                <Button variant="outline" onClick={add}>
                  <Plus size={16} /> {t('courseStations.addStation')}
                </Button>
                <AutoSaveIndicator status={auto.status} retry={auto.retry} />
              </div>
            </>
          )}
          <p className="text-xs text-muted-foreground pt-2">{t('courseStations.hint')}</p>
        </CardContent>
      </Card>
    </div>
  );
};

export default CourseStations;
